import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';

import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useLoadContext } from 'hooks/useLoadContext';
import { useToast } from 'hooks/useToaster';
import { updateTMS } from 'lib/api/updateTMS';
import { Load } from 'types/Load';
import { LoadAttributes } from 'types/LoadAttributes';
import { TMS } from 'types/enums/Integrations';
import {
  acceptLoadViewSuggestion,
  useApplyLoadViewSuggestion,
  useFocusLoadViewSuggestion,
} from 'utils/applyLoadViewSuggestion';
import {
  DEFAULT_MAX_VALIDATION_TOAST_LABELS,
  handleFormErrors,
} from 'utils/formValidation';

import { AvailableTabs } from '../constants';
import { TransportProFormLoad, TransportProSchedulingStop } from '../types';
import {
  buildTransportProLoadDefaults,
  buildTransportProUpdateLoadRequest,
} from '../utils';

type TransportProFormResult = {
  load?: Load;
  loadAttributes?: LoadAttributes;
};

type UseTransportProFormLogicArgs = {
  isCreateMode: boolean;
  load?: Load;
  activeTabs: string[];
  setActiveTabs: (tabs: string[]) => void;
};

async function refetchLoadFromContext(
  invalidateLoad: ReturnType<typeof useLoadContext>['invalidateLoad'],
  result?: TransportProFormResult
) {
  if (result?.load && result?.loadAttributes) {
    await invalidateLoad(result.load, result.loadAttributes);
  } else {
    await invalidateLoad();
  }
}

export function useTransportProFormLogic({
  isCreateMode,
  load,
  activeTabs,
  setActiveTabs,
}: UseTransportProFormLogicArgs) {
  const { toast } = useToast();
  const { invalidateLoad } = useLoadContext();
  const {
    currentState: { clickedSuggestion },
    setCurrentState,
  } = useContext(SidebarStateContext);
  const [isLoading, setIsLoading] = useState({ submitting: false });

  const defaultValues = useMemo(
    () => (load ? buildTransportProLoadDefaults(load) : undefined),
    [load]
  );

  const formMethods = useForm<TransportProFormLoad>({
    defaultValues: defaultValues as TransportProFormLoad,
    mode: 'onChange',
  });

  useEffect(() => {
    if (defaultValues) {
      formMethods.reset(defaultValues as TransportProFormLoad);
    }
  }, [defaultValues, formMethods]);

  const stopCount = defaultValues?.stops?.length ?? 0;
  useApplyLoadViewSuggestion(
    formMethods.setValue,
    clickedSuggestion,
    load?.freightTrackingID,
    !isCreateMode,
    defaultValues,
    stopCount
  );

  useFocusLoadViewSuggestion(
    clickedSuggestion,
    load?.freightTrackingID,
    setActiveTabs,
    !isCreateMode,
    Object.values(AvailableTabs)
  );

  const watchedStops = formMethods.watch('stops');
  const sortedStops = useMemo(() => {
    const stops = watchedStops ?? defaultValues?.stops ?? [];
    return [...stops].sort(
      (a, b) => (a.stopNumber ?? 0) - (b.stopNumber ?? 0)
    ) as TransportProSchedulingStop[];
  }, [defaultValues?.stops, watchedStops]);

  const handleFormSubmit: SubmitHandler<TransportProFormLoad> = useCallback(
    async (formValues) => {
      if (isCreateMode || !load?.ID) {
        if (!isCreateMode) {
          toast({
            description: 'Load ID is required to save appointments.',
            variant: 'destructive',
          });
        }
        return;
      }

      setIsLoading({ submitting: true });
      try {
        const req = buildTransportProUpdateLoadRequest(load, formValues);
        const res = await updateTMS(load.ID, req);

        if (!res.isOk()) {
          toast({
            description:
              res.error.message || 'Unable to save appointment changes.',
            variant: 'destructive',
          });
          if (res.error.load && res.error.loadAttributes) {
            await refetchLoadFromContext(invalidateLoad, {
              load: res.error.load,
              loadAttributes: res.error.loadAttributes,
            });
          }
          return;
        }

        await refetchLoadFromContext(invalidateLoad, res.value);
        toast({
          description: res.value.message || 'Appointment changes saved.',
          variant: 'success',
        });
        await acceptLoadViewSuggestion(
          clickedSuggestion,
          req.load,
          setCurrentState,
          undefined,
          TMS.TransportPro
        );
      } catch {
        toast({
          description: 'Unable to save appointment changes.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading({ submitting: false });
      }
    },
    [
      clickedSuggestion,
      invalidateLoad,
      isCreateMode,
      load,
      setCurrentState,
      toast,
    ]
  );

  const handleInvalidSubmit: SubmitErrorHandler<TransportProFormLoad> =
    useCallback(
      (errors) => {
        handleFormErrors(errors, {
          tmsName: TMS.TransportPro,
          maxLabelsToShow: DEFAULT_MAX_VALIDATION_TOAST_LABELS,
          activeTabs,
          setActiveTabs,
          toast,
        });
      },
      [activeTabs, setActiveTabs, toast]
    );

  return {
    formMethods,
    sortedStops,
    isLoading,
    handleFormSubmit: formMethods.handleSubmit(
      handleFormSubmit,
      handleInvalidSubmit
    ),
  };
}
